import { useState } from 'react'
import { useMount, useUpdateEffect } from 'ahooks'
import { useNavigate } from 'react-router-dom'
import { SideBar, Toast } from 'antd-mobile'

import styles from './index.module.less'
import iconSort from '@/assets//img/icon/sort.svg'
import iconFilter from '@/assets/img/icon/filter.svg'
import iconDropSelect from '@/assets/img/icon/drop_select.png'

import Icon from '@/components/Icon/Icon'
import SortModal from '@/components/SortModal/SortModal'
import ExpandPanelModal from '@/components/ExpandPanelModal/ExpandPanelModal'

import FilterModal from '../components/FilterModal/FilterModal'
import Tabbar from '../components/Tabbar'
import SearchBar from '../components/SearchBar'
import ProductList from '../components/ProductList/ProductList'
import HomeShare from '../components/Share/HomeShare'

import { apis, utils, uploadEt, productUtil, routesMap } from '@/utils'
import common from '@/utils/common'
import { t } from '@/utils/i18n'

import { useCorpInfoStore, useCorpSettingStore } from '@/stores'
import usePageTitle from '@/hooks/usePageTitle'

import type {
  categoryOneItem,
  SortItem,
  categoryTwoItem,
  RequestParams,
  FilterOption,
} from '@/types/product'

const { productApi } = apis
const { cloneDeep, capitalizeFirstLetter, isWebview, isEmptyObject } = utils
const { checkCorpChange } = common

// 获取默认分类
function getDefaultTab() {
  return [
    {
      categoryName: t('全部'),
      categoryId: 'all',
      categoryLevel: 0,
      children: null,
    },
    {
      categoryName: t('推荐'),
      categoryId: 'recommend',
      categoryLevel: 0,
      children: [
        {
          categoryName: t('新品'),
          categoryId: 'new',
          categoryLevel: 0,
          children: null,
        },
        {
          categoryName: t('爆品'),
          categoryId: 'hot',
          categoryLevel: 0,
          children: null,
        },
        {
          categoryName: t('促销'),
          categoryId: 'promotion',
          categoryLevel: 0,
          children: null,
        },
      ],
    },
  ]
}

function Products() {
  const { corpInfo } = useCorpInfoStore()
  const { corpSetting } = useCorpSettingStore()
  const navigate = useNavigate()

  const [listMode] = useState(true) // 列表模式
  const [showSortModal, setShowSortModal] = useState(false) // 排序弹窗
  const [showFilterModal, setShowFilterModal] = useState(false) // 刷选弹窗
  const [showExpandPanelModal, setShowExpandPanelModal] = useState(false)
  const [minPrice, setMinPrice] = useState('') // 最低价格
  const [maxPrice, setMaxPrice] = useState('') // 最高价格
  const [deliveryType, setDeliveryType] = useState<number[]>([])
  const [sortIndex, setSortIndex] = useState(-1) // 当前排序索引
  const [sortKey, setSortKey] = useState('') // 当前排序 key
  const [sortDirection, setSortDirection] = useState('DESC') // 当前排序方向
  const [levelOneData, setLevelOneData] = useState<categoryOneItem[]>([])
  const [levelOneSelectedIndex, setLevelOneSelectedIndex] = useState(0)
  const [activeLevelOneKey, setActiveLevelOneKey] = useState('all')
  const [levelTwoData, setLevelTwoData] = useState<categoryTwoItem[]>([])
  const [expandPanelData, setExpandPanelData] = useState<categoryTwoItem[]>([])
  const [levelTwoSelectedIndex, setLevelTwoSelectedIndex] = useState(-1)
  const [expandPanelSelectedIndex, setExpandPanelSelectedIndex] = useState(0)
  const [homeShareVisible, setHomeShareVisible] = useState(false)

  usePageTitle()
  checkCorpChange()

  // 商品列表搜索分类/推荐相关参数
  const [filter, setFilter] = useState<RequestParams>({
    otherType: 0, // 买过传 1
    productStatus: 0, // 推荐传 2
    recommendType: 0, // 推荐时传 推荐类型：1 新品 2 爆品 4 促销 99 多货品分享
    categoryId: '0',
    categoryLevel: 0,
    combineType: 0,
    customerId: '0',
    interfaceVersion: 1,
    onScan: false,
    sortDirection: '1',
    sortKey: '5',
    unionMall: 0,
  })

  const {
    error,
    msg,
    runAsync: onGetCategoryList,
  } = productApi.useAllList({
    corpId: corpInfo.corpId,
    industryId: corpInfo.industryId,
  })

  const onSearch = (text: string) => {
    filter.searchText = text
    setFilter(cloneDeep(filter))
  }

  // 获取 App 扫码结果
  const getScanValue = (text: string) => {
    navigate(`/products/search?text=${text}`)
  }

  window.getScanValue = getScanValue

  // 切换排序
  function toggleShowSortModal() {
    hideAllModal('showSortModal')
    setShowSortModal(!showSortModal)
  }

  // 切换刷选
  function toggleShowFilterModal() {
    hideAllModal('showFilterModal')
    setShowFilterModal(!showFilterModal)
  }

  // 获取货品分类
  async function getProductCategory() {
    let categoryList = await onGetCategoryList()

    if (error) {
      Toast.show({ content: msg })
      return
    }

    if (!categoryList || isEmptyObject(categoryList)) {
      categoryList = []
    }

    if (Array.isArray(categoryList)) {
      setLevelOneData([...getDefaultTab(), ...categoryList])
    }
  }

  // 排序筛选改变
  function handleChangeSort(sortItem: SortItem, index: number) {
    setSortIndex(index)
    setSortKey(sortItem.key)
    setSortDirection(sortItem.sortDirection || 'DESC')
    toggleShowSortModal()
  }

  // 价格过滤改变
  function handlePriceFilterChange(option: FilterOption) {
    setMinPrice(option.minPrice)
    setMaxPrice(option.maxPrice)
    setDeliveryType(option.deliveryType)
    toggleShowFilterModal()
  }

  function hideAllModal(key?: string) {
    switch (key) {
      case 'showSortModal':
        setShowFilterModal(false)
        setShowExpandPanelModal(false)
        break
      case 'showFilterModal':
        setShowSortModal(false)
        setShowExpandPanelModal(false)
        break
      case 'showExpandPanelModal':
        setShowSortModal(false)
        setShowFilterModal(false)
        break
      default:
        setShowSortModal(false)
        setShowFilterModal(false)
        setShowExpandPanelModal(false)
        break
    }
  }

  function toggleShowExpandPanelModal() {
    if (showExpandPanelModal) {
      setShowExpandPanelModal(!showExpandPanelModal)
      return
    }

    // 在隐藏状态下，显示所有二级分类
    const copyLevelTwoData = Array.from(levelTwoData) // 扩展面板显示所有二级分类

    copyLevelTwoData.forEach((item) => {
      item.level = 2
    })

    setExpandPanelData(copyLevelTwoData)
    hideAllModal('showExpandPanelModal')
    setExpandPanelSelectedIndex(levelTwoSelectedIndex)
    setShowExpandPanelModal(true)
  }

  function handleSideBarChange(categoryId: string) {
    setActiveLevelOneKey(categoryId)
    const index = levelOneData.findIndex(
      (item) => item.categoryId === categoryId,
    )
    setLevelOneSelectedIndex(index)
    const levelOneItem = levelOneData[index]
    let newLevelTwoData: categoryTwoItem[] = []

    if (levelOneItem) {
      newLevelTwoData = Array.from(levelOneItem.children || [])
    }

    hideAllModal()

    // 有二级分类时
    if (newLevelTwoData.length) {
      // 分类不为推荐则添加全部
      if (categoryId !== 'recommend') {
        newLevelTwoData.unshift({
          categoryName: t('全部'),
          categoryId,
          categoryLevel: 1,
          children: null,
        })
      }

      setLevelTwoData(newLevelTwoData)

      const newExpandPanelData = Array.from(newLevelTwoData).map((item) => {
        item.level = 2
        return item
      })

      setExpandPanelData(newExpandPanelData) // 扩展面板显示二级分类
      setExpandPanelSelectedIndex(0)
      setLevelTwoSelectedIndex(0)
      setShowExpandPanelModal(true)
    } else {
      setExpandPanelSelectedIndex(0)
      setLevelTwoSelectedIndex(0)
      setLevelTwoData([])
      setExpandPanelData([])
    }
  }

  function handleLevelTwoChange(index: number) {
    const { categoryId, categoryLevel, children } = levelTwoData[index]
    let newShowExpandPanelModal = false
    // 二级分类为改变时
    if (index === levelTwoSelectedIndex) {
      // 有三级分类则展示三级分类
      if (expandPanelData.length) {
        setShowExpandPanelModal(true)
      }

      return
    }

    // 点击 ”全部“ 分类时扩展面板显示的仍旧是所有二级分类
    if (categoryLevel === 1) {
      const newExpandPanelData = Array.from(levelTwoData).map((item) => {
        item.level = 2
        return item
      })

      setExpandPanelData(newExpandPanelData)
      setExpandPanelSelectedIndex(0)
      newShowExpandPanelModal = true
    } else {
      const newExpandPanelData: categoryTwoItem[] = children
        ? Array.from(children)
        : []

      // 点击“其它”二级分类时，如果有三级分类，扩展面板显示该二级分类下的所有三级分类
      if (newExpandPanelData.length) {
        newExpandPanelData.unshift({
          categoryId,
          categoryName: t('全部'),
          categoryLevel: 2,
          children: null,
        })

        newExpandPanelData.forEach((item: any) => {
          item.level = 3
        })

        setExpandPanelSelectedIndex(0)
        newShowExpandPanelModal = true
      }

      setExpandPanelData(newExpandPanelData)
    }

    hideAllModal('showExpandPanelModal')
    setShowExpandPanelModal(newShowExpandPanelModal)
    setLevelTwoSelectedIndex(index)
  }

  // 扩展面板上的二级分类改变时
  function handleExpandPanelChange(index: number) {
    const { level } = expandPanelData[index]

    hideAllModal()

    setExpandPanelSelectedIndex(index)
    // 如果当前是二级分类
    if (level === 2) {
      setLevelTwoSelectedIndex(index)

      const { categoryId, children } = levelTwoData[index]
      const threeChildren: categoryTwoItem[] = Array.from(children || [])

      // 如果有三级分类，扩展面板显示该三级分类
      if (threeChildren.length) {
        threeChildren.unshift({
          categoryId,
          categoryName: t('全部'),
          categoryLevel: 2,
          children: null,
        })

        threeChildren.forEach((item) => {
          item.level = 3
        })

        setExpandPanelData(threeChildren)
        setExpandPanelSelectedIndex(0)
        setShowExpandPanelModal(true)
      }
    }
  }

  const LevelTwoItem = () => {
    return levelTwoData.map((item, index) => {
      const levelTwoClass = `${styles['level-two-item']} ${
        index === levelTwoSelectedIndex ? styles['selected'] : ''
      }`

      return (
        <div
          className={levelTwoClass}
          key={item.categoryId}
          onClick={() => handleLevelTwoChange(index)}
        >
          {capitalizeFirstLetter(item.categoryName)}
        </div>
      )
    })
  }

  // 初始化
  async function init() {
    await getProductCategory()
    const sortOptions = productUtil.initSortOptions(
      corpSetting.cloudProductSort,
      corpSetting.salesOn,
      corpSetting.salesSlipOn,
    ) // 初始化排序选项

    setSortIndex(sortOptions.sortActive.activeIndex as number)
    setSortKey(sortOptions.sortActive.activeKey)
    setSortDirection(sortOptions.sortActive.sortDirection as string)
  }

  useMount(async () => {
    uploadEt({ event_type: 4 }) // 埋点，全部货品页浏览次数
    await init()
  })

  // 设置请求参数
  useUpdateEffect(() => {
    // const newFilter = cloneDeep(filter)
    let category // 当前选中的分类
    let otherType = 0 // 买过 传1
    let productStatus = 0 // 推荐传2
    let recommendType = 0 // 推荐时传 推荐类型：1新品 2爆品 4促销 99多货品分享
    let categoryId = '0' // 分类id
    let categoryLevel = 0 // 分类级别

    if (expandPanelData.length) {
      category = expandPanelData[expandPanelSelectedIndex]
    } else if (levelTwoData.length) {
      category = levelTwoData[levelTwoSelectedIndex]
    } else if (levelOneData.length) {
      category = levelOneData[levelOneSelectedIndex]
    } else {
      // 无一级分类时
      category = { categoryId: '0', categoryLevel: 0 }
    }

    const { categoryId: newCategoryId, categoryLevel: newCategoryLevel } =
      category

    // 买过
    if (newCategoryId === 'bought') {
      otherType = 1
    } else if (newCategoryId === 'optimize') {
      // 多货品分享
      productStatus = 2
      recommendType = 99
    } else if (newCategoryId === 'new') {
      // 推荐-新品
      productStatus = 2
      recommendType = 1
    } else if (newCategoryId === 'hot') {
      // 推荐-爆品
      productStatus = 2
      recommendType = 2
    } else if (newCategoryId === 'promotion') {
      // 推荐-促销
      productStatus = 2
      recommendType = 4
    } else if (newCategoryId !== 'all') {
      // 其它（非全部）
      categoryId = newCategoryId
      categoryLevel = newCategoryLevel || 0
    }

    filter.combineType = 0
    filter.sortKey = sortKey
    filter.sortDirection = sortDirection === 'ASC' ? '0' : '1'
    filter.otherType = otherType
    filter.productStatus = productStatus
    filter.recommendType = recommendType
    filter.categoryId = categoryId
    filter.categoryLevel = categoryLevel

    if (deliveryType.length) {
      if (deliveryType.length === 1) {
        filter.deliveryType = deliveryType[0]
      } else {
        delete filter.deliveryType
      }
    } else {
      delete filter.deliveryType
    }

    if (minPrice) {
      filter.minPrice = minPrice
    } else {
      delete filter.minPrice
    }

    if (maxPrice) {
      filter.maxPrice = maxPrice
    } else {
      delete filter.maxPrice
    }

    setFilter(cloneDeep(filter))
  }, [
    expandPanelData,
    expandPanelSelectedIndex,
    levelOneData,
    levelOneSelectedIndex,
    levelTwoData,
    levelTwoSelectedIndex,
    maxPrice,
    minPrice,
    sortDirection,
    sortKey,
    deliveryType,
  ])
  // 一级分类改变时

  function openMenuModal() {
    if (
      window.webview?.openDrawerMenu &&
      typeof window.webview.openDrawerMenu === 'function'
    )
      window.webview.openDrawerMenu()
  }

  // 分享页面
  function onSharePage() {
    setHomeShareVisible(true)
  }

  // 切换语言
  function onSwitchLanguage() {
    navigate(routesMap.LangSetting)
  }

  return (
    <div className={styles['home-wrap']}>
      {/* 搜索栏 */}
      <div className={styles['searchbar-box']}>
        {isWebview() ? (
          <Icon
            name="menu"
            className={styles['more-menu']}
            size={44}
            color="black"
            onClick={openMenuModal}
          />
        ) : (
          <Icon
            name="switch-lang"
            className={styles['more-menu']}
            size={48}
            color="black"
            onClick={onSwitchLanguage}
          />
        )}

        <SearchBar
          className={styles['search-bar']}
          customizeStyle={{ backgroundColor: '#F2F2F2' }}
          onSearch={onSearch}
        />

        <Icon
          name="share"
          className={styles['more-menu']}
          size={48}
          color="black"
          onClick={onSharePage}
        />
      </div>

      <div className={styles['product-bd']}>
        <div className={styles['category-wrap'] + ' category'}>
          <SideBar
            className={styles['category-category']}
            activeKey={activeLevelOneKey}
            onChange={handleSideBarChange}
            style={{ '--width': '100%', '--background-color': '#f7f7f7' }}
          >
            {levelOneData.map((item) => (
              <SideBar.Item
                key={item.categoryId}
                title={capitalizeFirstLetter(item.categoryName)}
                className={styles['category-item']}
              />
            ))}
          </SideBar>
        </div>

        <div className={styles['product-list']}>
          <div className={styles['product-toolbar']}>
            {/* 排序 */}
            <div
              className={styles['product-toolbar-sort']}
              onClick={toggleShowSortModal}
            >
              <span className={styles['product-toolbar-sort-text']}>
                {t('排序')}
              </span>
              <img
                className={styles['product-toolbar-sort-icon']}
                src={iconSort}
                title="sort"
              />
            </div>

            {/* 筛选 */}
            <div
              className={styles['product-toolbar-filter']}
              onClick={toggleShowFilterModal}
            >
              <span className={styles['product-toolbar-filter-text']}>
                {t('筛选')}
              </span>
              <img
                className={styles['product-toolbar-filter-icon']}
                src={iconFilter}
                title="filter"
              />
            </div>
          </div>
          {levelTwoData.length ? (
            <div className={styles['level-two-container']}>
              <div className={styles['level-two']}>
                <LevelTwoItem />
              </div>
              <div className={styles['level-two-icon']}>
                <img
                  src={iconDropSelect}
                  onClick={toggleShowExpandPanelModal}
                  title="drop-select"
                />
              </div>
            </div>
          ) : (
            ''
          )}

          {/* 扩展面板 */}
          {showExpandPanelModal ? (
            <ExpandPanelModal
              expandPanelSelectedIndex={expandPanelSelectedIndex}
              onToggleShowExpandPanelModal={toggleShowExpandPanelModal}
              expandPanelData={expandPanelData}
              onExpandPanelChange={handleExpandPanelChange}
            />
          ) : (
            ''
          )}

          {/* 排序弹窗 */}
          {showSortModal ? (
            <SortModal
              sortIndex={sortIndex}
              sortDirection={sortDirection}
              onToggleShowSortModal={toggleShowSortModal}
              onChangeSort={handleChangeSort}
            />
          ) : (
            ''
          )}

          {/* 价格过滤弹窗 */}
          {showFilterModal ? (
            <FilterModal
              min={minPrice}
              max={maxPrice}
              deliveryType={deliveryType}
              onChangeFilter={handlePriceFilterChange}
              onToggleShowFilterModal={toggleShowFilterModal}
            />
          ) : (
            ''
          )}

          <div className={styles['product-list-container']}>
            <ProductList
              listMode={listMode}
              requestParams={filter}
              isFilterPage={false}
            />
          </div>
        </div>
      </div>

      {homeShareVisible && (
        <HomeShare
          onClose={() => {
            setHomeShareVisible(false)
          }}
        />
      )}

      <Tabbar activeKey="AlbumCategory" />
    </div>
  )
}

export default Products
